import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { GrpcMetadata } from '@ngx-grpc/common';
import { BehaviorSubject, finalize, map, Observable, of, tap } from 'rxjs';
import { Empty } from '@ngx-grpc/well-known-types';
import { StorageService } from '../../../services/storage.service';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../../authentication/service/auth.service';
import { IdentityEndpointClient } from '../../../protos/generated/admin.pbsc';
import { CreateGroupRequest, DisableGroupStatusRequest, GetUserRequest, RegisterRequest, ResetPasswordRequest, UpdateGroupRequest, UpdateUserRequest, UserGroupDto, UsersListRequest } from '../../../protos/generated/admin.pb';

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  token: string | null;
  //loading state for userGroups
  userGroupsLoading = signal(false);


  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private usersService: IdentityEndpointClient,
    private storageService: StorageService,
    private authService: AuthService
  ) {
    this.token = storageService.getItem('token');
  }

  //React to changes when update a user 
  private isUserUpdated = signal(false);
  notifyUserUpdate() {
    this.isUserUpdated.set(true);
  }
  getUserUpdateSignal() {
    return this.isUserUpdated;
  }

  //React to changes when updating userGroups
  private isUserGroupChanged = signal(false);
  
  notifyUserGroupChanged() {

  }

  getIsUserGroupChanged() {
    return this.isUserGroupChanged;
  }

  //React to changes when enable/disable user
  private isUserDisabledStatusChanged = signal(false);
  notifyUserDisabledStatusChange() {
    this.isUserDisabledStatusChanged.set(!this.isUserDisabledStatusChanged())
  }

  getUserDisabledStatusSignal() {
    return this.isUserDisabledStatusChanged;
  }

  //generate metadata for the grpc call
  private getAuthMetadata(): GrpcMetadata {
    const metadata = new GrpcMetadata();
    const token = this.storageService.getItem('token');
    if (this.token) {
      metadata.set('Authorization', `Bearer ${this.token}`);
    }
    return metadata;
  }

  getUsersList(page: number, pageSize: number, search: string): Observable<any> {
    const usersListData = new UsersListRequest({
      pageSize,
      page,
      search,
    });

    const metadata = this.authService.getMetadata();
    return this.usersService.usersList(usersListData, metadata);
  }


  getUser(userId: number): Observable<any> {
    const metadata = this.getAuthMetadata();
    const getUserRequest = new GetUserRequest();
    getUserRequest.id = userId
    return this.usersService.getUser(getUserRequest, metadata);
  }

  updateUser(updateUserRequest: UpdateUserRequest): Observable<any> {
    const metadata = this.getAuthMetadata()
    return this.usersService.update(updateUserRequest, metadata)
  }

  getAllUserGroups(): Observable<any> {
    this.userGroupsLoading.set(true);
    if (!isPlatformBrowser(this.platformId)) {
      this.userGroupsLoading.set(false);
      return of({ userGroups: [] });
    }
    const metadata = this.authService.getMetadata();
    return this.usersService.getAllGroups(new Empty(), metadata).pipe(
      finalize(() => this.userGroupsLoading.set(false))
    );
  }


  registerUser(registerRequest: RegisterRequest) {
    const metadata = this.getAuthMetadata();
    return this.usersService.register(registerRequest, metadata)
  }

  getAllRoles() {
    const metadata = this.getAuthMetadata();
    return this.usersService.getAllRoles(new Empty(), metadata)
  }

  addNewUserGroup(createGroupRequest: CreateGroupRequest) {
    const metadata = this.getAuthMetadata();
    return this.usersService.createGroup(createGroupRequest, metadata)
  }

  updateUserGroup(updateGroupRequest: UpdateGroupRequest): Observable<any> {
    const metadata = this.getAuthMetadata();
    return this.usersService.updateGroup(updateGroupRequest, metadata)
  }

  resetPassword(resetPasswordRequest: ResetPasswordRequest) {
    const metadata = this.getAuthMetadata();
    return this.usersService.resetPassword(resetPasswordRequest, metadata)
  }


  enableUser(request: GetUserRequest) {
    const metadata = this.getAuthMetadata();
    return this.usersService.enable(request, metadata)
  }

  disableUser(request: GetUserRequest) {
    const metadata = this.getAuthMetadata();
    return this.usersService.disable(request, metadata)
  }

  changeUserGroupDisableStatus(disableGroupStatusRequest: DisableGroupStatusRequest) {
    const metadata = this.getAuthMetadata();
    return this.usersService.changeDisableStatusGroup(disableGroupStatusRequest, metadata)
  }

}

